// FHEVM SDK utilities for frontend
import { BrowserProvider, ethers, JsonRpcProvider } from "ethers";

// Import @zama-fhe/relayer-sdk - use static import (like secret-vote-box)
// Note: Vite config excludes this from optimization
import { createInstance, initSDK, SepoliaConfig } from "@zama-fhe/relayer-sdk/bundle";
import type { FhevmInstance } from "@zama-fhe/relayer-sdk/bundle";

// Import @fhevm/mock-utils for localhost mock FHEVM
// Note: Use dynamic import to avoid including in production bundle
let MockFhevmInstance: any = null;
let userDecryptHandleBytes32: any = null;

export interface EncryptedInput {
  handles: string[];
  inputProof: string;
}

let fhevmInstance: FhevmInstance | null = null;
let isSDKInitialized = false;
let isMockInstance = false;

/**
 * Initialize FHEVM instance
 * Local network (31337): Uses @fhevm/mock-utils + Hardhat plugin
 * Sepolia (11155111): Uses @zama-fhe/relayer-sdk
 */
export async function initializeFHEVM(chainId?: number): Promise<FhevmInstance> {
  if (!fhevmInstance) {
    // Check window.ethereum
    if (typeof window === "undefined" || !(window as any).ethereum) {
      throw new Error("window.ethereum is not available. Please install MetaMask.");
    }

    // Get chainId first
    let currentChainId = chainId;
    if (!currentChainId) {
      try {
        const chainIdHex = await (window as any).ethereum.request({ method: "eth_chainId" });
        currentChainId = parseInt(chainIdHex, 16);
      } catch (error) {
        console.error("[FHEVM] Failed to get chainId:", error);
        currentChainId = 31337;
      }
    }

    console.log("[FHEVM] Current chain ID:", currentChainId);

    // Initialize SDK for Sepolia (following secret-vote-box pattern)
    if (currentChainId === 11155111 && !isSDKInitialized) {
      console.log("[FHEVM] Initializing FHE SDK for Sepolia...");
      
      try {
        // Simple initialization like secret-vote-box
        await initSDK();
        isSDKInitialized = true;
        console.log("[FHEVM] ✅ SDK initialized successfully");
      } catch (error: any) {
        console.error("[FHEVM] SDK initialization failed:", error);
        // Continue execution, don't throw error, let createInstance handle it
        console.warn("[FHEVM] Continuing with createInstance...");
      }
    }

    // Local network: Use Mock FHEVM
    if (currentChainId === 31337) {
      const localhostRpcUrl = "http://localhost:8545";
      
      try {
        console.log("[FHEVM] Fetching FHEVM metadata from Hardhat node...");
        const provider = new JsonRpcProvider(localhostRpcUrl);
        const metadata = await provider.send("fhevm_relayer_metadata", []);
        
        console.log("[FHEVM] Metadata:", metadata);
        
        if (metadata && metadata.ACLAddress && metadata.InputVerifierAddress && metadata.KMSVerifierAddress) {
          // Use @fhevm/mock-utils to create mock instance
          if (!MockFhevmInstance || !userDecryptHandleBytes32) {
            const mockUtils = await import("@fhevm/mock-utils");
            MockFhevmInstance = mockUtils.MockFhevmInstance;
            userDecryptHandleBytes32 = mockUtils.userDecryptHandleBytes32;
            console.log("[FHEVM] ✅ Loaded mock-utils:", {
              hasMockInstance: !!MockFhevmInstance,
              hasDecryptFunc: !!userDecryptHandleBytes32
            });
          }
          
          console.log("[FHEVM] Creating MockFhevmInstance with addresses:", {
            ACL: metadata.ACLAddress,
            InputVerifier: metadata.InputVerifierAddress,
            KMSVerifier: metadata.KMSVerifierAddress,
          });
          
          const mockInstance = await MockFhevmInstance.create(provider, provider, {
            aclContractAddress: metadata.ACLAddress,
            chainId: 31337,
            gatewayChainId: 55815,
            inputVerifierContractAddress: metadata.InputVerifierAddress,
            kmsContractAddress: metadata.KMSVerifierAddress,
            verifyingContractAddressDecryption: "0x5ffdaAB0373E62E2ea2944776209aEf29E631A64",
            verifyingContractAddressInputVerification: "0x812b06e1CDCE800494b79fFE4f925A504a9A9810",
          });
          
          fhevmInstance = mockInstance;
          isMockInstance = true;
          console.log("[FHEVM] ✅ Mock FHEVM instance created successfully!");
          return mockInstance;
        } else {
          throw new Error("FHEVM metadata is incomplete");
        }
      } catch (error: any) {
        console.error("[FHEVM] Failed to create Mock instance:", error);
        throw new Error(
          `Local Hardhat node FHEVM initialization failed: ${error.message}\n\n` +
          `Please ensure:\n` +
          `1. Hardhat node is running (npx hardhat node)\n` +
          `2. @fhevm/hardhat-plugin is imported in hardhat.config.ts\n` +
          `3. Restart Hardhat node and retry`
        );
      }
    }
    
    // Sepolia network: Use official SDK with MetaMask provider
    else if (currentChainId === 11155111) {
      try {
        console.log("[FHEVM] Creating Sepolia FHEVM instance...");
        
        // Use MetaMask's provider instead of public RPC to avoid CORS issues
        if (typeof window === "undefined" || !(window as any).ethereum) {
          throw new Error("MetaMask not detected. Please install MetaMask to use Sepolia network.");
        }
        
        // Create config using MetaMask provider (no CORS issues)
        const config = {
          ...SepoliaConfig,
          network: (window as any).ethereum,  // Use MetaMask provider
        };
        
        fhevmInstance = await createInstance(config);
        console.log("[FHEVM] ✅ Sepolia FHEVM instance created successfully!");
      } catch (error: any) {
        console.error("[FHEVM] ❌ Sepolia instance creation failed:", error);
        throw new Error(
          `Failed to create Sepolia FHEVM instance: ${error.message || "Unknown error"}`
        );
      }
    }
    
    else {
      throw new Error(`Unsupported network (Chain ID: ${currentChainId}). Please switch to local network (31337) or Sepolia (11155111).`);
    }
  }
  
  return fhevmInstance;
}

// Encrypt a number (euint32)
export async function encryptNumber(
  fhevm: FhevmInstance,
  contractAddress: string,
  userAddress: string,
  number: number
): Promise<EncryptedInput> {
  try {
    const encryptedInput = fhevm
      .createEncryptedInput(contractAddress, userAddress)
      .add32(number);
    
    const encrypted = await encryptedInput.encrypt();
    
    // Convert Uint8Array to hex strings for contract calls
    // externalEuint32 is represented as bytes32 in the ABI, so we need to pad the handle to 32 bytes
    const handles = encrypted.handles.map(handle => {
      const hexHandle = ethers.hexlify(handle);
      // Pad to 32 bytes (64 hex characters) if needed
      if (hexHandle.length < 66) { // 0x + 64 hex chars = 66
        const padded = hexHandle.slice(2).padStart(64, '0');
        return `0x${padded}`;
      }
      // If longer than 32 bytes, take the first 32 bytes
      if (hexHandle.length > 66) {
        return hexHandle.slice(0, 66);
      }
      return hexHandle;
    });
    
    return {
      handles,
      inputProof: ethers.hexlify(encrypted.inputProof),
    };
  } catch (error: any) {
    console.error("Error encrypting number:", error);
    throw new Error(`Failed to encrypt number: ${error.message || "Unknown error"}`);
  }
}

// Decrypt an encrypted number (euint32)
export async function decryptNumber(
  fhevm: FhevmInstance,
  encryptedValue: string,
  contractAddress: string,
  signer: any,
  chainId?: number
): Promise<number> {
  try {
    const isLocalNetwork = chainId === 31337;
    const isSepoliaNetwork = chainId === 11155111;
    
    console.log("[FHEVM] Decrypting number:", {
      chainId,
      isLocalNetwork,
      isSepoliaNetwork,
      encryptedValueLength: encryptedValue?.length
    });
    
    if (isLocalNetwork) {
      // For local network, use userDecryptHandleBytes32 from @fhevm/mock-utils
      if (!userDecryptHandleBytes32) {
        const mockUtils = await import("@fhevm/mock-utils");
        userDecryptHandleBytes32 = mockUtils.userDecryptHandleBytes32;
      }
      
      if (!userDecryptHandleBytes32) {
        throw new Error("userDecryptHandleBytes32 not available. Please ensure @fhevm/mock-utils is properly initialized.");
      }
      
      const provider = new JsonRpcProvider("http://localhost:8545");
      const userAddress = await signer.getAddress();
      
      console.log("[FHEVM] Decrypting on localhost with userDecryptHandleBytes32");
      const value = await userDecryptHandleBytes32(
        provider,
        signer,
        contractAddress,
        encryptedValue,
        userAddress
      );
      
      console.log("[FHEVM] ✅ Decrypted:", value);
      return Number(value);
    } else if (isSepoliaNetwork) {
      // For Sepolia network, use userDecrypt with keypair and EIP712 signature
      console.log("[FHEVM] Decrypting on Sepolia with userDecrypt");
      
      // Generate keypair
      const keypair = (fhevm as any).generateKeypair();
      console.log("[FHEVM] ✅ Generated keypair");
      
      // Get user address
      const userAddress = await signer.getAddress();
      
      // Prepare handle-contract pair
      const handleContractPairs = [{
        handle: encryptedValue,
        contractAddress: contractAddress,
      }];
      
      // Contract addresses array
      const contractAddresses = [contractAddress];
      
      // Create EIP712 typed data
      const startTimeStamp = Math.floor(Date.now() / 1000).toString();
      const durationDays = "10";
      
      const eip712 = (fhevm as any).createEIP712(
        keypair.publicKey,
        contractAddresses,
        startTimeStamp,
        durationDays
      );
      
      console.log("[FHEVM] 🔑 Requesting signature...");
      
      // Request signature from user
      const signature = await signer.signTypedData(
        eip712.domain,
        { UserDecryptRequestVerification: eip712.types.UserDecryptRequestVerification },
        eip712.message
      );
      
      console.log("[FHEVM] ✅ Got signature, decrypting...");
      
      // Decrypt with user's signature
      const result = await (fhevm as any).userDecrypt(
        handleContractPairs,
        keypair.privateKey,
        keypair.publicKey,
        signature.replace("0x", ""),
        contractAddresses,
        userAddress,
        startTimeStamp,
        durationDays
      );
      
      const decryptedValue = result[encryptedValue];
      console.log("[FHEVM] ✅ Decrypted on Sepolia:", decryptedValue, "type:", typeof decryptedValue);
      
      // Convert BigInt or number to number
      if (typeof decryptedValue === 'bigint') {
        return Number(decryptedValue);
      } else if (typeof decryptedValue === 'number') {
        return decryptedValue;
      } else if (decryptedValue !== undefined && decryptedValue !== null) {
        return Number(decryptedValue);
      } else {
        console.warn("[FHEVM] Decrypted value is undefined or null, returning 0");
        return 0;
      }
    } else {
      throw new Error(`Unsupported network for decryption. ChainId: ${chainId}. Only local (31337) and Sepolia (11155111) are supported.`);
    }
  } catch (error: any) {
    console.error("[FHEVM] Error decrypting number:", error);
    throw new Error(`Failed to decrypt number: ${error.message || "Unknown error"}`);
  }
}

// Reset FHEVM instance (useful for testing or reconnection)
export function resetFHEVMInstance() {
  fhevmInstance = null;
  isSDKInitialized = false;
  isMockInstance = false;
  createInstance = null;
  initSDK = null;
  SepoliaConfig = null;
  FhevmInstanceType = null;
}

// Get current FHEVM instance
export function getFHEVMInstance(): FhevmInstance | null {
  return fhevmInstance;
}
